import React, { useState } from 'react';
import { View, StyleSheet, Alert } from 'react-native';
import { Text, IconButton, useTheme, Menu } from 'react-native-paper';
import { MoreVertical, Heart, Edit3, Trash2 } from 'lucide-react-native';
import { formatDistanceToNow } from 'date-fns';
import type { Comment } from '@coffeeplexity/shared';

interface CommentItemProps {
  comment: Comment;
  currentUserId?: string;
  onEdit?: (comment: Comment) => void;
  onDelete?: (commentId: string) => void;
  onLike?: (commentId: string) => void;
  onUnlike?: (commentId: string) => void;
  isLikeLoading?: boolean;
  hideLikeButton?: boolean;
}

export function CommentItem({
  comment,
  currentUserId,
  onEdit,
  onDelete,
  onLike,
  onUnlike,
  isLikeLoading = false,
  hideLikeButton = false,
}: CommentItemProps) {
  const theme = useTheme();
  const [menuVisible, setMenuVisible] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const isOwner = currentUserId === comment.user_id;
  const isLiked = comment.is_liked_by_user || false;

  const handleLikePress = async () => {
    if (isProcessing || isLikeLoading) return;

    setIsProcessing(true);
    try {
      if (isLiked) {
        await onUnlike?.(comment.id);
      } else {
        await onLike?.(comment.id);
      }
    } finally {
      // Add a small delay to prevent rapid double-clicks
      setTimeout(() => setIsProcessing(false), 300);
    }
  };

  const handleDeletePress = () => {
    setMenuVisible(false);
    Alert.alert(
      'Delete Comment',
      'Are you sure you want to delete this comment?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => onDelete?.(comment.id),
        },
      ]
    );
  };

  const handleEditPress = () => {
    setMenuVisible(false);
    onEdit?.(comment);
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.surface }]}>
      <View style={styles.header}>
        <View style={styles.userInfo}>
          <Text
            variant='labelLarge'
            style={[styles.username, { color: theme.colors.onSurface }]}
          >
            {comment.user_name}
          </Text>
          <Text
            variant='bodySmall'
            style={{ color: theme.colors.onSurfaceVariant }}
          >
            {formatDistanceToNow(new Date(comment.created_at), {
              addSuffix: true,
            })}
          </Text>
        </View>

        {isOwner && (
          <Menu
            visible={menuVisible}
            onDismiss={() => setMenuVisible(false)}
            anchor={
              <IconButton
                icon={() => (
                  <MoreVertical
                    size={20}
                    color={theme.colors.onSurfaceVariant}
                  />
                )}
                size={20}
                onPress={() => setMenuVisible(true)}
              />
            }
          >
            <Menu.Item
              leadingIcon={() => (
                <Edit3 size={18} color={theme.colors.onSurface} />
              )}
              onPress={handleEditPress}
              title='Edit'
            />
            <Menu.Item
              leadingIcon={() => (
                <Trash2 size={18} color={theme.colors.error} />
              )}
              onPress={handleDeletePress}
              title='Delete'
              titleStyle={{ color: theme.colors.error }}
            />
          </Menu>
        )}
      </View>

      <Text
        variant='bodyMedium'
        style={[styles.content, { color: theme.colors.onSurface }]}
      >
        {comment.text || comment.content}
      </Text>

      <View style={styles.footer}>
        {!hideLikeButton && (
          <View style={styles.likeContainer}>
            <IconButton
              icon={() => (
                <Heart
                  size={18}
                  color={
                    isLiked ? theme.colors.error : theme.colors.onSurfaceVariant
                  }
                  fill={isLiked ? theme.colors.error : 'none'}
                />
              )}
              size={18}
              onPress={handleLikePress}
              disabled={isProcessing || isLikeLoading}
            />
            {comment.likes_count > 0 && (
              <Text
                variant='bodySmall'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                {comment.likes_count}
              </Text>
            )}
          </View>
        )}

        {comment.replies_count > 0 && (
          <Text
            variant='bodySmall'
            style={{ color: theme.colors.onSurfaceVariant }}
          >
            {comment.replies_count}{' '}
            {comment.replies_count === 1 ? 'reply' : 'replies'}
          </Text>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  userInfo: {
    flex: 1,
  },
  username: {
    fontWeight: '600',
    marginBottom: 2,
  },
  content: {
    marginBottom: 8,
    lineHeight: 20,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  likeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: -8,
  },
});
